import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TextField, Button } from "@mui/material";
import InfiniteScrollTrigger from "components/infinite-scroll/InfiniteScrollTrigger";
import PredictorCard from "components/card/PredictorCard";
import { skeletonGenerator } from "components/infinite-scroll/helpers";
import ProgramSkeleton from "components/infinite-scroll/skeletons/ArticleSkeleton";
import { getPredictors } from "api-algolia/predictors.api";
import useSearch from "hooks/algolia/useSearch";

const PredictorsList = ({
  predictors,
  currentUserId,
  currentUserDisplayName,
  currentUserType,
  isLoadingNextPage,
  hasMoreItems,
  getNextPage,
  page,
  setPage,
  createPredictor,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setSearchText, searchText, searchResult, searchLoading } = useSearch({
    getData: getPredictors,
  });

  const isSearching = searchText.trim().length > 0;
  const hasSearchResults = searchResult && searchResult.length > 0;

  return (
    <div
      className="w-full h-full overflow-y-auto p-16"
      style={{ background: "#e2e2e2" }}
    >
      <div className="m-auto">
        <div className="flex">
          <TextField
            placeholder="Որոնում"
            type="text"
            autoComplete="off"
            sx={{
              backgroundColor: "#F5F7FF",
              borderRadius: "14px",
              "& fieldset": { border: "none" },
            }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full md:w-fit"
          />

          {(currentUserType === "admin" || currentUserType === "editor") && (
            <div className="w-full grid justify-items-end">
              <Button
                variant="outlined"
                onClick={async () => {
                  const predictor = await createPredictor({
                    createdAt: Date.now(),
                    author: {
                      id: currentUserId,
                      displayName: currentUserDisplayName,
                    },
                  });
                  navigate(`/predictors/manage/${predictor.id}`);
                }}
              >
                {t("predictors.predictorsList.createNewPredictor")}
              </Button>
            </div>
          )}
        </div>

        <div className="py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          {isSearching && (
            <>
              {searchLoading && skeletonGenerator(<ProgramSkeleton />)}

              {!searchLoading &&
                hasSearchResults &&
                searchResult.map((predictor, index) => (
                  <PredictorCard
                    key={index}
                    item={predictor}
                    onCardClick={() =>
                      navigate(`/predictors/manage/${predictor.id}`)
                    }
                  />
                ))}

              {!searchLoading && !hasSearchResults && (
                <div className="col-span-full text-center text-gray-500 py-16">
                  Կանխատեսողներ չեն գտնվել
                </div>
              )}
            </>
          )}

          {!isSearching && (
            <>
              {predictors.map((predictor, index) => (
                <PredictorCard
                  key={index}
                  item={predictor}
                  onCardClick={() =>
                    navigate(`/predictors/manage/${predictor.id}`)
                  }
                />
              ))}

              {isLoadingNextPage && skeletonGenerator(<ProgramSkeleton />)}

              <InfiniteScrollTrigger
                isLoading={isLoadingNextPage}
                hasMoreItems={hasMoreItems}
                callback={getNextPage}
                page={page}
                setPage={setPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PredictorsList;
